// src/prototype/components/oir/report/OIRReportView.tsx
// ============================================================
// Orchestrator for the Portfolio Intelligence Report tab.
// UX Improvements (March 2026):
//   - OIRPageHeader / redundant title band removed entirely
//   - Export + Share buttons live in the EATabBar row (tab-level)
//   - Section order updated: Financial Impact now precedes
//     Strategy Decision Canvas (value first, then selection)
//   - 2-column layout: 240px sticky index rail + content
//   - Section anchor IDs for OIRReportIndex scroll nav
//   - Shared scenario state across Financial Impact + Strategy Canvas (DVA-UI-018)
// ============================================================

import React, { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { OIRReportIndex } from './OIRReportIndex';
import { OIRSectionExecutiveSummary } from './OIRSectionExecutiveSummary';
import { OIRSectionStrategicContext } from './OIRSectionStrategicContext';
import { OIRSectionOpportunityLandscape } from './OIRSectionOpportunityLandscape';
import { OIRSectionStrategyDecisionCanvas } from './OIRSectionStrategyDecisionCanvas';
import { OIRSectionFinancialImpact } from './OIRSectionFinancialImpact';
import { OIRSectionExecutionRoadmap, type ApiRoadmapBuckets } from './OIRSectionExecutionRoadmap';
import type { PortfolioScenario } from './portfolioScenario';

export type { PortfolioScenario };

// ── Types ──────────────────────────────────────────────────────────────────
interface SurfacedReason {
  contextLabel?: string;
  contextValue?: string;
  relevance: string;
}

interface Opportunity {
  id: string;
  rank: number;
  title: string;
  category: string;
  description?: string;
  implementationEffort: 'Low' | 'Medium' | 'High';
  timeToValue: string;
  roiEstimate: { low: number; high: number; unit?: string };
  confidence: string | number;
  primaryValueDriver?: string;
  surfacedReasons?: SurfacedReason[];
}

export interface OIRReportViewProps {
  clientName: string;
  rankedOpportunities: Opportunity[];
  selectedOpportunities: Opportunity[];
  top10: Opportunity[];
  portfolioRangeLabel: string;
  shortlistedRangeLabel: string;
  apiWaterfall?: {
    conservative: { benefits: number; costs: number; net: number };
    base: { benefits: number; costs: number; net: number };
    aggressive: { benefits: number; costs: number; net: number };
  };
  avgTimeToValue?: number;
  finalistCount: number;
  totalOpportunities: number;
  personalizationData?: {
    profile: number;
    surveys: number;
    documents: number;
    initiatives: number;
  };
  strategicPriorities?: string[];
  knownConstraints?: string[];
  /** When provided, these are shown as the Top 3 Recommended instead of top10.slice(0,3). */
  top3Opps?: Opportunity[];
  /** When provided, the Execution Roadmap section uses API data instead of rank heuristics. */
  apiRoadmapBuckets?: ApiRoadmapBuckets;
  onOpenExplorationTab: () => void;
  onContinueToExecute: () => void;
  onNavigate: (path: string, id: string) => void;
  onBack: () => void;
}

// ── Section Anchor Wrapper ─────────────────────────────────────────────────
const Section: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => (
  <div id={id} style={{ scrollMarginTop: 'var(--oir-sticky-offset, 284px)' }}>
    {children}
  </div>
);

// ── Component ──────────────────────────────────────────────────────────────
export const OIRReportView: React.FC<OIRReportViewProps> = ({
  clientName,
  rankedOpportunities,
  selectedOpportunities,
  top10,
  top3Opps,
  portfolioRangeLabel,
  shortlistedRangeLabel,
  apiWaterfall,
  avgTimeToValue,
  finalistCount,
  totalOpportunities,
  personalizationData,
  strategicPriorities,
  knownConstraints,
  apiRoadmapBuckets,
  onNavigate,
  onBack,
}) => {
  // Shared across Financial Impact + Strategy Decision Canvas (DVA-UI-018)
  const [scenario, setScenario] = useState<PortfolioScenario>('base');

  return (
    <div>
      <div className="flex gap-6 items-start">
        <OIRReportIndex />

        <div className="flex-1 min-w-0">
          <Section id="oir-exec-summary">
            <OIRSectionExecutiveSummary
              clientName={clientName}
              portfolioRangeLabel={portfolioRangeLabel}
              shortlistedRangeLabel={shortlistedRangeLabel}
              selectedCount={finalistCount}
              totalOpportunities={totalOpportunities}
              top10={top10}
              top3Opps={top3Opps}
              selectedOpportunities={selectedOpportunities}
            />
          </Section>

          <Section id="oir-strategic-context">
            <OIRSectionStrategicContext
              clientName={clientName}
              personalizationData={personalizationData}
              strategicPriorities={strategicPriorities}
              knownConstraints={knownConstraints}
            />
          </Section>

          <Section id="oir-opportunity-landscape">
            <OIRSectionOpportunityLandscape rankedOpportunities={rankedOpportunities} />
          </Section>

          <Section id="oir-financial-impact">
            <OIRSectionFinancialImpact
              top10={top10}
              selectedOpportunities={selectedOpportunities}
              portfolioRangeLabel={portfolioRangeLabel}
              shortlistedRangeLabel={shortlistedRangeLabel}
              apiWaterfall={apiWaterfall}
              avgTimeToValue={avgTimeToValue}
              scenario={scenario}
              onScenarioChange={setScenario}
            />
          </Section>

          <Section id="oir-strategy-decision-canvas">
            <OIRSectionStrategyDecisionCanvas
              top10={top10}
              onNavigate={onNavigate}
              scenario={scenario}
              onScenarioChange={setScenario}
            />
          </Section>

          <Section id="oir-execution-roadmap">
            <OIRSectionExecutionRoadmap
              rankedOpportunities={rankedOpportunities}
              selectedOpportunities={selectedOpportunities}
              apiRoadmapBuckets={apiRoadmapBuckets}
            />
          </Section>

          <div className="pt-4 border-t border-[#e5e7eb] mt-2">
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-4 py-2 text-[#717182] hover:text-[#18365E] hover:bg-white rounded-lg transition-all text-sm font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Personalize
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
